import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Dispatch, SetStateAction } from 'react';
import * as apiService from 'api/apiServices';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { useToast } from 'context/ToastContext';
import { useService } from './context/ServiceProvider';
import useDeleteService from './hooks/hookDeleteService';
import ServiceFilterStatus from './ServiceToolBar/ServiceFilterStatus';
import CatalogAdminDropdown from '../CatalogCategory/CatalogAdminDropdown';
import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import EToolTip from 'components/others/EToolTip';
import { useNeedPay } from 'modules/admin';
import SolarIcon from 'components/icons/SolarIcon';

interface ServiceToolBarProps {
    fetchServices: (force?: boolean) => Promise<unknown>;
    filterStatus: number[];
    setFilterStatus: Dispatch<SetStateAction<number[]>>;
    filterCategoryIds: number[];
    setFilterCategoryIds: (ids: number[]) => void;
}

const ServiceToolBar = ({
    fetchServices,
    filterStatus,
    setFilterStatus,
    filterCategoryIds,
    setFilterCategoryIds,
}: ServiceToolBarProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Services', 'common']);
    const { showToast } = useToast();
    const {
        setShowEditService,
        serviceCheck,
        setServiceCheck,
        setShowSpinner,
        triggerListReset,
        triggerListRefresh,
        showUpdated,
        toggleListColumn,
    } = useService();
    const { archiveService } = useDeleteService();
    const { needPay } = useNeedPay();

    const cbArchiveService = async () => {
        setShowSpinner(true);
        const resActive = await apiService.setStat(serviceCheck, 0);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchServices(true);
        setShowSpinner(false);
        setServiceCheck([]);
    };

    const handleClickActive = async () => {
        setShowSpinner(true);
        const resActive = await apiService.setStat(serviceCheck, 1);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchServices(true);
        setShowSpinner(false);
        setServiceCheck([]);
    };

    return (
        <div className="mb-3" style={{ minWidth: '100px' }}>
            <div className="page-toolbar-actions">
                {hasUserAccess(auth?.user, 'prodEdit') && (
                    <>
                        <Button
                            className="fs-14"
                            size="sm"
                            variant="success"
                            onClick={(e) => {
                                e.currentTarget.blur();
                                if (needPay()) return;
                                setShowEditService(0);
                            }}
                        >
                            <SolarIcon name="plus" className="fs-14 p-0" />
                            <span className="d-none d-md-inline ms-1">{t('toolAddService')}</span>
                        </Button>

                        <EToolTip title={t('toolArchive')}>
                            <span className="d-inline-block">
                                <Button
                                    className="fs-14 px-2 toolbar-icon-btn"
                                    size="sm"
                                    variant="danger"
                                    disabled={!serviceCheck?.length}
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        archiveService(cbArchiveService);
                                    }}
                                >
                                    <SolarIcon name="archive" className="fs-14 p-0" />
                                </Button>
                            </span>
                        </EToolTip>

                        <EToolTip title={t('toolActive')}>
                            <span className="d-inline-block">
                                <Button
                                    className="fs-14 px-2 toolbar-icon-btn"
                                    size="sm"
                                    variant="success"
                                    disabled={!serviceCheck?.length}
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        void handleClickActive();
                                    }}
                                >
                                    <SolarIcon name="check2" className="fs-14 p-0" />
                                </Button>
                            </span>
                        </EToolTip>
                    </>
                )}

                {!!auth?.user?.admin && <CatalogAdminDropdown kind="service" />}

                <ListToolBarActions
                    filters={
                        <ServiceFilterStatus
                            filterStatus={filterStatus}
                            setFilterStatus={setFilterStatus}
                            filterCategoryIds={filterCategoryIds}
                            setFilterCategoryIds={setFilterCategoryIds}
                        />
                    }
                    onReset={triggerListReset}
                    onRefresh={triggerListRefresh}
                    columns={{
                        items: [
                            { id: 'updated', label: t('fields.updated'), visible: showUpdated },
                        ],
                        onToggle: (id) => toggleListColumn(id as 'updated'),
                    }}
                />
            </div>
        </div>
    );
};

export default ServiceToolBar;
